import { gql, useQuery } from '@apollo/client';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import React, { useState } from 'react';
import TextInput from 'shared/components/TextInput';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { Channel } from 'shared/types/graphql';
import { useUpdateTeamChannel } from './hooks/useUpdateTeamChannel';

const GET_TEAM_CHANNEL = gql`
  query getTeamChannel($where: ChannelWhereUniqueInput!) {
    findUniqueChannel(where: $where) {
      id
      friendlyName
    }
  }
`;

type EditTeamChannelModalProps = {
  channelId: string;
  isOpen: boolean;
  onClose: () => void;
};

const EditTeamChannelModal = ({ channelId, isOpen, onClose }: EditTeamChannelModalProps): JSX.Element => {
  const [friendlyName, setFriendlyName] = useState('');
  const [friendlyNameValidationMessage, setFriendlyNameValidationMessage] = useState('');

  useQuery<{ findUniqueChannel: Channel }>(GET_TEAM_CHANNEL, {
    variables: { where: { id: channelId } },
    fetchPolicy: GraphQLFetchPolicies.CacheOnly,
    onCompleted: (data) => {
      setFriendlyName(data?.findUniqueChannel?.friendlyName || '');
    },
  });

  const [updateTeamChannel, { loading: isSavingTeamChannel }] = useUpdateTeamChannel();

  const handleSaveChangesClick = async (): Promise<void> => {
    if (!friendlyName) {
      setFriendlyNameValidationMessage('Please provide a name for this channel');
      return;
    }
    await updateTeamChannel({ variables: { where: { id: channelId }, data: { friendlyName } } });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader pb={1}>
        <Heading size="sm" mb={4}>
          Edit Team Channel
        </Heading>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody maxHeight="500px">
        <TextInput
          name="friendlyName"
          label="Channel Name"
          required={true}
          autoFocus={true}
          value={friendlyName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setFriendlyName(event.target.value)}
        />
        {friendlyNameValidationMessage && (
          <Text as="p" mt={2} mb={0} color="text.danger" size="xs">
            {friendlyNameValidationMessage}
          </Text>
        )}
        <ModalFooterButtons
          px={0}
          submitText={'Save Changes'}
          onCancel={onClose}
          isLoading={isSavingTeamChannel}
          onSubmit={handleSaveChangesClick}
        />
      </ModalBody>
    </Modal>
  );
};

export default EditTeamChannelModal;
